import { useCallback } from 'react';
import { Form, getIn, useFormikContext } from 'formik';

import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';

import type { EMPTY_STRING } from '@e-socna/empty';
import type { GenericStatusId } from '@e-socna/data-layer';

import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useUiContext } from '../../contexts/UiContext/hooks/useUiContext.hook';
import { ConfirmBeforeExitForm } from '../../components/ConfirmBeforeExitForm';
import { FORM_LAYOUT_STYLE, FormLayout } from '../../components/FormLayout';
import { IdSelect } from '../../components/IdSelect';
import type { IdOption } from '../../components/IdSelect';

import { MemoBeforeSubmitForm } from '../MemoBeforeSubmit';
import { memoAwareSubmitHandler } from '../MemoBeforeSubmit/utils/memo-aware-submit-handler.util';
import type { MemosState } from '../MemoBeforeSubmit';
import { AlertMessages } from '../../components/AlertMessages';

import type { GenericAdminFormValues } from './types/generic-admin-form-values.type';

interface Props {
  readonly askMemoBeforeSubmit: boolean;
  readonly clearMemos?: () => void;
  readonly formError: readonly string[] | null;
  readonly memos?: MemosState;
  readonly onCancel: () => unknown;
  readonly setMemos?: (
    setMemosFn: ((memos: MemosState) => MemosState) | MemosState,
  ) => void;
  readonly setMustBlockAction?: (mustBlock: boolean) => void;
}

export const GenericAdminFormFields = ({
  askMemoBeforeSubmit,
  clearMemos,
  formError,
  memos,
  onCancel: handleCancel,
  setMemos,
  setMustBlockAction,
}: Readonly<Props>) => {
  const { ui } = useUiContext();
  const { genericStatuses } = useReferencesContext();

  const {
    dirty,
    errors,
    handleBlur,
    handleSubmit: formikSubmit,
    isSubmitting,
    isValid,
    setFieldValue,
    submitForm,
    touched,
    values,
  } = useFormikContext<GenericAdminFormValues>();

  const submitLabel = 'Enregistrer';
  const isLoading = isSubmitting || ui.isBackgroundOperationRunning;

  const handleStatusIdChange = useCallback(
    (id: GenericStatusId | typeof EMPTY_STRING) => {
      void setFieldValue('statusId', id);
    },
    [setFieldValue],
  );

  const showMemoDialog = useCallback(() => {
    if (setMemos) {
      setMemos((oldMemos) => ({
        ...oldMemos,
        isOpenMemoDialog: true,
      }));
    }
  }, [setMemos]);

  return (
    <Form
      onSubmit={memoAwareSubmitHandler({
        askMemo: showMemoDialog,
        mustAskMemo: askMemoBeforeSubmit && dirty && isValid,
        submit: formikSubmit,
      })}
      // eslint-disable-next-line react/forbid-component-props
      style={FORM_LAYOUT_STYLE}
    >
      <ConfirmBeforeExitForm setMustBlockAction={setMustBlockAction} />
      {!!(askMemoBeforeSubmit && memos && setMemos && clearMemos) && (
        <MemoBeforeSubmitForm
          clearMemos={clearMemos}
          isLoading={isLoading}
          memos={memos}
          onConfirm={submitForm}
          setMemos={setMemos}
          submitLabel={submitLabel}
        />
      )}
      <FormLayout
        actions={
          <Stack direction="row" spacing={2}>
            <Button
              disabled={!isValid}
              loading={isLoading}
              size="large"
              type="submit"
              variant="contained"
            >
              {submitLabel}
            </Button>
            <Button onClick={handleCancel} size="large" variant="outlined">
              Annuler
            </Button>
          </Stack>
        }
        error={
          !!formError && <AlertMessages messages={formError} severity="error" />
        }
        title="Administration"
      >
        <Grid container spacing={2}>
          <Grid size={12} sx={{ pt: 1, pb: 1 }}>
            <IdSelect<GenericStatusId, IdOption<GenericStatusId>>
              error={!!(errors.statusId && touched.statusId)}
              helperText={
                !!(errors.statusId && touched.statusId) &&
                (getIn(errors, 'statusId') as string)
              }
              id="status-id"
              label="Statut"
              name="statusId"
              onBlur={handleBlur}
              onChange={handleStatusIdChange}
              options={genericStatuses.map((genericStatus) => ({
                id: genericStatus._id,
                label: genericStatus.label,
              }))}
              required
              value={values.statusId}
            />
          </Grid>
        </Grid>
      </FormLayout>
    </Form>
  );
};
